// Unidade de venda do produto — quilo e litro (pedido do dono, 30/09/2026).
// Regra única do sistema, a mesma do servidor (backend/services/unidade_venda.py):
// o item é vendido por peso/volume quando o produto está em quilo ou litro E
// marcado como divisível — o teste que o Balcão, o Delivery e o cardápio usam
// para abrir o campo de peso. Produto em "L" vendido inteiro ("Açaí 500 ml")
// é por unidade. Relatórios: somar sem cortar a parte quebrada, mostrar
// "1,250 kg" e nunca somar quilo com unidade no mesmo total.
const MEDIDAS = { kg: "kg", l: "L" };

/** "kg" | "L" quando o produto é vendido por peso/volume; null por unidade. */
export function medidaDoProduto(produto) {
  if (!produto?.is_divisible) return null;
  return MEDIDAS[String(produto.sale_unit || "").trim().toLowerCase()] || null;
}

/** Medida vinda do servidor (`unidade` da linha do relatório) ou do cadastro. */
export function medidaDaLinha(linha, produtos) {
  if (linha?.unidade) return MEDIDAS[String(linha.unidade).toLowerCase()] || null;
  if (linha && "unidade" in linha) return null;
  const pid = linha?.product_id || linha?.id;
  const p = pid && Array.isArray(produtos) ? produtos.find((x) => String(x.id) === String(pid)) : null;
  return medidaDoProduto(p || linha);
}

/** Produto do item: `produtos` pode ser lista ou mapa { id: produto }. */
function produtoDoItem(item, produtos) {
  const pid = item?.id || item?.product_id;
  if (!pid || !produtos) return null;
  if (Array.isArray(produtos)) return produtos.find((p) => String(p.id) === String(pid)) || null;
  return produtos[pid] || produtos[String(pid)] || null;
}

/** Medida do item do pedido: pelo cadastro (quando dá) ou pelo próprio item.
 *  Item antigo sem a marca de divisível: quilo/litro só com quantidade quebrada. */
export function medidaDoItem(item, produtos) {
  const produto = produtoDoItem(item, produtos);
  if (produto && ("sale_unit" in produto || "is_divisible" in produto)) return medidaDoProduto(produto);
  if (item?.unidade) return MEDIDAS[String(item.unidade).toLowerCase()] || null;
  if (item?.is_divisible != null) return medidaDoProduto(item);
  const q = Number(item?.quantidade ?? item?.quantity ?? 1);
  return Number.isInteger(q) ? null : medidaDoProduto({ ...item, is_divisible: true });
}

const numero = (v) => {
  const n = typeof v === "string" ? Number(v.replace(",", ".")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const decimal = (n, casas) => n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });

/** "1,250 kg" / "0,500 L" por peso/volume; "3" (ou "2,5") por unidade. */
export function formatarQtd(qtd, medida) {
  const n = numero(qtd);
  if (medida) return `${decimal(n, 3)} ${medida}`;
  if (Number.isInteger(n)) return n.toLocaleString("pt-BR");
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

/** Na linha do item: "1,250 kg" ou "2x". */
export function qtdDoItem(qtd, medida) {
  return medida ? formatarQtd(qtd, medida) : `${formatarQtd(qtd, null)}x`;
}

/** "N itens" (30/09/2026): produto por peso/volume conta 1 por linha — 1,250 kg
 *  de picanha não é "1,25 itens"; por unidade soma a quantidade. Quantidade
 *  quebrada sem medida conhecida (produto apagado) também conta 1. */
export function contarItens(itens, produtos) {
  return (itens || []).filter((i) => i && !i.deleted).reduce((s, i) => {
    const q = Number(i.quantidade || i.quantity || i.qty || 1) || 1;
    return s + (medidaDoItem(i, produtos) || !Number.isInteger(q) ? 1 : q);
  }, 0);
}

/**
 * O item gravado no pedido não guarda a unidade de venda: completa
 * `sale_unit`/`is_divisible` pelo cadastro (por `id`/`product_id`) nos itens
 * que não têm. Devolve a MESMA lista quando não há o que completar (pode ser
 * usado dentro de setState sem gerar render à toa). 30/09/2026.
 */
export function completarUnidadeDoCadastro(itens, produtos) {
  if (!Array.isArray(itens) || !produtos) return itens;
  let mudou = false;
  const novos = itens.map((item) => {
    if (!item || item.sale_unit != null || item.is_divisible != null) return item;
    const p = produtoDoItem(item, produtos);
    if (!p || !("sale_unit" in p || "is_divisible" in p)) return item;
    mudou = true;
    return { ...item, sale_unit: p.sale_unit || "un", is_divisible: !!p.is_divisible };
  });
  return mudou ? novos : itens;
}

/** Soma separada por medida: { un, kg, L } — quilo não entra na conta de unidades. */
export function totaisPorMedida(linhas, campo = "quantidade", produtos) {
  const out = { un: 0, kg: 0, L: 0 };
  for (const ln of linhas || []) out[medidaDaLinha(ln, produtos) || "un"] += numero(ln?.[campo]);
  return out;
}

/** "12 un · 3,750 kg · 1,500 L" (só o que tem). Sem nada: "0". */
export function textoTotais(totais) {
  const partes = [];
  if (totais?.un) partes.push(`${formatarQtd(totais.un, null)} un`);
  if (totais?.kg) partes.push(formatarQtd(totais.kg, "kg"));
  if (totais?.L) partes.push(formatarQtd(totais.L, "L"));
  return partes.length ? partes.join(" · ") : "0";
}
